/**
 * documentView — what a reviewer can ask of the document they are looking at.
 *
 * Two axes, and they are not independent: you read either the ORIGINAL upload
 * or the REVISED document the accepted decisions produced, and a revised one
 * can be read CLEAN (as it would ship) or as a REDLINE (struck-out original
 * beside the replacement). Clean/Redline means nothing on the original, which
 * is why the style toggle only appears in revised mode.
 *
 * The rules lived twice — once in the viewer and once in its side-panel
 * wrapper — and they had already drifted apart in how the download was named.
 * They live here now, as pure functions, so the toolbar and the viewer cannot
 * disagree about what a file type supports or what a saved copy is called.
 */

/** The original upload, or the document the decisions produced. */
export type ViewSource = 'original' | 'revised';

/** How a revised document renders: shipped, or with the changes marked. */
export type RevisionStyle = 'clean' | 'redline';

/**
 * The file types a revision can be generated for.
 *
 * The backend renders revisions by rewriting the document, which needs a
 * document — a scan, a spreadsheet or a text dump has nowhere to put a
 * redline. Everything else is read as-is, with no toggles offered, rather
 * than being offered a mode that would fail.
 */
export const REVISABLE_FILE_TYPES = ['docx', 'doc', 'pdf'];

/** True when Original/Revised (and so Clean/Redline) applies to this file. */
export const supportsRevision = (fileType: string | null | undefined): boolean =>
  REVISABLE_FILE_TYPES.includes((fileType || '').toLowerCase().replace(/^\./, ''));

/**
 * What a downloaded copy is called.
 *
 * The mode goes in the NAME, before the extension: a reviewer who saves the
 * clean revision and the redline of the same contract ends up with two files
 * they can tell apart, and both still open in Word. Downloading the original
 * gives back exactly the uploaded name — annotating that would be a lie about
 * which file it is.
 */
export const downloadName = (
  name: string,
  source: ViewSource,
  style: RevisionStyle,
): string => {
  if (source !== 'revised') return name;
  const suffix = `_${style}`;
  // No extension to insert before — the suffix goes on the end, rather than
  // being silently dropped as the regex-only version did.
  return /(\.[^.]+)$/.test(name) ? name.replace(/(\.[^.]+)$/, `${suffix}$1`) : `${name}${suffix}`;
};

/**
 * WHERE a finding lives in the document you are currently reading.
 *
 * The same finding sits behind different words depending on the view, which
 * is why one hard-coded search string could only ever land in one of them:
 *
 *   - ORIGINAL — nothing has been applied, so the clause still reads as it
 *     was written. `original_text` is present, and so is the injected
 *     `finding-<uuid>` anchor the viewer prefers.
 *   - REVISED / REDLINE — the replacement sits BESIDE the struck-out
 *     original, so the original wording is still on the page and is still
 *     the safest thing to search for.
 *   - REVISED / CLEAN — an accepted (or modified) finding's original wording
 *     has been rewritten away, taking its anchor with it. The only text that
 *     exists there is the text that replaced it.
 *
 * `section_reference` rides along in every case as the fallback the viewer
 * tries before falling back to text, because a clause number survives being
 * rewritten when its sentence does not.
 *
 * `pending` lets a decision that has not yet come back from the server say
 * what it is about to be, so the jump can be aimed at the reloaded document
 * rather than the one still on screen.
 */
export interface FindingScrollAnchor {
  /** Text to look for when neither anchor nor section reference is found. */
  searchText: string;
  /** The clause number/heading — tried before text. */
  sectionReference: string;
}

interface FindingLike {
  decision?: string | null;
  section_reference?: string | null;
  original_text?: string | null;
  suggested_redline?: string | null;
  modified_text?: string | null;
  issue_description?: string | null;
}

export const findingScrollAnchor = (
  finding: FindingLike,
  source: ViewSource,
  style: RevisionStyle,
  pending?: { decision?: string | null; modifiedText?: string | null },
): FindingScrollAnchor => {
  const decision = pending?.decision ?? finding.decision ?? null;
  const applied = decision === 'accepted' || decision === 'modified';
  /* Only the clean revision actually loses the original wording. */
  const rewritten = source === 'revised' && style === 'clean' && applied;

  const searchText = rewritten
    ? (pending?.modifiedText
      || finding.modified_text
      || finding.suggested_redline
      || finding.original_text
      || finding.issue_description
      || '')
    : (finding.original_text || finding.issue_description || '');

  return {
    searchText,
    sectionReference: finding.section_reference || '',
  };
};
